import React, { useMemo } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { accessibleTextProps } from '../../utils/accessibility-utils';
import WelcomeMarkerIcon from './WelcomeMarkerIcon';
import WelcomeHostHouseIcon from './WelcomeHostHouseIcon';
import WelcomeHeadline from './WelcomeHeadline';
import WelcomeFitText from './WelcomeFitText';
import { useWelcomeResponsiveLayout } from './useWelcomeResponsiveLayout';

interface WelcomeHomeContentProps {
  isHost: boolean;
  isAuthenticated: boolean;
  onFindCharger: () => void;
  onHostPress: () => void;
  onViewMyChargers: () => void;
  onSignIn: () => void;
  onOpenMenu: () => void;
}

const SUBHEADLINE =
  'Find charging near you or turn your charger into passive income with MeanderEV';
const CHARGE_CARD_TITLE = 'I need to charge';
const HOST_CARD_TITLE = 'I have a charger';
const VIEW_CHARGERS_TITLE = 'View my Chargers';
const FIND_CHARGER_BUTTON = 'FIND A CHARGER';
const HOST_CHARGER_BUTTON = 'HOST MY CHARGER';
const VIEW_CHARGERS_BUTTON = 'VIEW MY CHARGERS';

const WelcomeHomeContent: React.FC<WelcomeHomeContentProps> = ({
  isHost,
  isAuthenticated,
  onFindCharger,
  onHostPress,
  onViewMyChargers,
  onSignIn,
  onOpenMenu,
}) => {
  const insets = useSafeAreaInsets();
  const { content, cardTitleFit, buttonLabelFit } = useWelcomeResponsiveLayout();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          width: '100%',
          paddingHorizontal: content.containerPaddingHorizontal,
          paddingTop: content.containerPaddingTop,
        },
        headerSection: {
          alignItems: 'center',
          marginBottom: content.headerMarginBottom,
          width: '100%',
        },
        subheadline: {
          marginTop: content.subheadlineMarginTop,
          width: '100%',
          alignSelf: 'stretch',
          fontFamily: 'PlusJakartaSans-Regular',
          fontSize: content.subheadlineFontSize,
          fontWeight: '400',
          lineHeight: content.subheadlineLineHeight,
          textAlign: 'center',
          color: '#FFFFFF',
        },
        cardsRow: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: content.cardColumnGap,
          width: '100%',
          marginTop: content.cardsRowMarginTop,
        },
        actionCard: {
          flex: 1,
          alignItems: 'stretch',
          justifyContent: 'flex-start',
        },
        cardIconWrap: {
          alignItems: 'center',
          width: '100%',
          marginBottom: content.cardIconMarginBottom,
        },
        cardTitle: {
          marginTop: content.cardTitleMarginTop,
          alignSelf: 'stretch',
        },
        cardDescription: {
          marginTop: content.cardDescriptionMarginTop,
          alignSelf: 'stretch',
          width: '100%',
          fontFamily: 'PlusJakartaSans-Regular',
          fontSize: content.cardDescriptionFontSize,
          fontWeight: '400',
          lineHeight: content.cardDescriptionLineHeight,
          textAlign: 'center',
          color: '#FFFFFF',
        },
        freeEmphasis: {
          color: '#64E280',
          fontFamily: 'PlusJakartaSans-Bold',
          fontWeight: '700',
          textDecorationLine: 'underline',
          textDecorationColor: '#64E280',
        },
        findChargerButton: {
          marginTop: content.actionButtonMarginTop,
          width: '100%',
          minHeight: content.actionButtonHeight,
          borderRadius: 999,
          backgroundColor: '#FFE54F',
          alignItems: 'center',
          justifyContent: 'center',
          paddingHorizontal: content.actionButtonPaddingHorizontal,
          paddingVertical: content.actionButtonPaddingVertical,
        },
        hostButton: {
          marginTop: content.actionButtonMarginTop,
          width: '100%',
          minHeight: content.actionButtonHeight,
          borderRadius: 999,
          backgroundColor: '#64E280',
          alignItems: 'center',
          justifyContent: 'center',
          paddingHorizontal: content.actionButtonPaddingHorizontal,
          paddingVertical: content.actionButtonPaddingVertical,
        },
        footerSection: {
          marginTop: content.footerMarginTop,
          alignItems: 'center',
          width: '100%',
        },
        footerPrompt: {
          marginBottom: content.footerPromptMarginBottom,
          fontFamily: 'PlusJakartaSans-Regular',
          fontSize: content.footerPromptFontSize,
          lineHeight: content.footerPromptLineHeight,
          color: '#8D8E90',
          textAlign: 'center',
        },
        footerButton: {
          width: '100%',
          minHeight: content.actionButtonHeight,
          borderRadius: 999,
          borderWidth: 1.5,
          borderColor: '#FFFFFF',
          alignItems: 'center',
          justifyContent: 'center',
          paddingHorizontal: content.actionButtonPaddingHorizontal * 2,
          paddingVertical: content.actionButtonPaddingVertical,
        },
        footerButtonText: {
          letterSpacing: 0.5,
        },
      }),
    [content]
  );

  const hostCardTitle = isHost ? VIEW_CHARGERS_TITLE : HOST_CARD_TITLE;
  const hostButtonLabel = isHost ? VIEW_CHARGERS_BUTTON : HOST_CHARGER_BUTTON;
  const footerLabel = isAuthenticated ? 'OPEN MENU' : 'SIGN IN';
  const footerButtonTextWidth =
    content.footerButtonWidth - content.actionButtonPaddingHorizontal * 4;

  return (
    <View
      style={[
        styles.container,
        { paddingBottom: Math.max(insets.bottom, content.containerPaddingBottomMin) },
      ]}
    >
      <View style={styles.headerSection}>
        <WelcomeHeadline />
        <Text {...accessibleTextProps.body} style={styles.subheadline}>
          {SUBHEADLINE}
        </Text>
      </View>

      <View style={styles.cardsRow}>
        <View
          style={[
            styles.actionCard,
            { width: content.cardWidth, maxWidth: content.cardWidth },
          ]}
        >
          <View style={styles.cardIconWrap}>
            <WelcomeMarkerIcon
              width={content.markerIcon.width}
              height={content.markerIcon.height}
            />
          </View>
          <WelcomeFitText
            text={CHARGE_CARD_TITLE}
            availableWidth={content.cardTextWidth}
            fitOptions={cardTitleFit}
            lineHeightRatio={1.25}
            fontFamily="Poppins-Bold"
            fontWeight="700"
            color="#FFFFFF"
            style={styles.cardTitle}
            accessibilityLabel={CHARGE_CARD_TITLE}
          />
          <Text {...accessibleTextProps.body} style={styles.cardDescription}>
            Find and start charging at thousands of stations.
          </Text>
          <Pressable
            style={styles.findChargerButton}
            onPress={onFindCharger}
            accessibilityRole="button"
            accessibilityLabel="Find a charger"
          >
            <WelcomeFitText
              text={FIND_CHARGER_BUTTON}
              availableWidth={content.buttonTextWidth}
              fitOptions={buttonLabelFit}
              lineHeightRatio={1.35}
              fontFamily="PlusJakartaSans-Medium"
              fontWeight="500"
              color="#000000"
            />
          </Pressable>
        </View>

        <View
          style={[
            styles.actionCard,
            { width: content.cardWidth, maxWidth: content.cardWidth },
          ]}
        >
          <View style={styles.cardIconWrap}>
            {isHost ? (
              <MaterialCommunityIcons
                name="ev-station"
                size={content.hostStationIconSize}
                color="#64E280"
              />
            ) : (
              <WelcomeHostHouseIcon
                width={content.houseIcon.width}
                height={content.houseIcon.height}
              />
            )}
          </View>
          <WelcomeFitText
            text={hostCardTitle}
            availableWidth={content.cardTextWidth}
            fitOptions={cardTitleFit}
            lineHeightRatio={1.25}
            fontFamily="Poppins-Bold"
            fontWeight="700"
            color="#FFFFFF"
            style={styles.cardTitle}
            accessibilityLabel={hostCardTitle}
          />
          <Text {...accessibleTextProps.body} style={styles.cardDescription}>
            {isHost ? (
              'Manage your chargers and track earnings.'
            ) : (
              <>
                List your charger{' '}
                <Text style={styles.freeEmphasis}>for free</Text>
                {' '}and earn passive income!
              </>
            )}
          </Text>
          <Pressable
            style={styles.hostButton}
            onPress={isHost ? onViewMyChargers : onHostPress}
            accessibilityRole="button"
            accessibilityLabel={hostButtonLabel}
          >
            <WelcomeFitText
              text={hostButtonLabel}
              availableWidth={content.buttonTextWidth}
              fitOptions={buttonLabelFit}
              lineHeightRatio={1.35}
              fontFamily="PlusJakartaSans-Medium"
              fontWeight="500"
              color="#000000"
            />
          </Pressable>
        </View>
      </View>

      <View style={styles.footerSection}>
        {!isAuthenticated && (
          <Text {...accessibleTextProps.label} style={styles.footerPrompt}>
            Already have an account?
          </Text>
        )}
        <Pressable
          style={[
            styles.footerButton,
            { maxWidth: content.footerButtonWidth },
          ]}
          onPress={isAuthenticated ? onOpenMenu : onSignIn}
          accessibilityRole="button"
          accessibilityLabel={footerLabel}
        >
          <WelcomeFitText
            text={footerLabel}
            availableWidth={footerButtonTextWidth}
            fitOptions={buttonLabelFit}
            lineHeightRatio={1.35}
            fontFamily="PlusJakartaSans-Medium"
            fontWeight="500"
            color="#FFFFFF"
            style={styles.footerButtonText}
          />
        </Pressable>
      </View>
    </View>
  );
};

export default WelcomeHomeContent;
